import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, RefreshCw, Edit2, Trash2, Settings } from 'lucide-react';
import axios from '../lib/axios';
import Layout from '../components/Layout';

export default function LocationManagement() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', zuperPropertyUid: '' });
  const [activeFilter, setActiveFilter] = useState('active');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () => {
      const { data } = await axios.get('/api/locations');
      return data;
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => axios.post('/api/locations/sync-zuper'),
    onSuccess: () => queryClient.invalidateQueries(['admin-locations']),
  });

  const createMutation = useMutation({
    mutationFn: (locData) => axios.post('/api/locations', locData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-locations']);
      setShowCreateModal(false);
      setForm({ name: '', address: '', zuperPropertyUid: '' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...updates }) => axios.put(`/api/locations/${id}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-locations']);
      setEditingLocation(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/locations/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['admin-locations']),
  });

  const allLocations = data?.locations || [];
  const locations = allLocations.filter(loc => {
    if (activeFilter === 'active') return loc.is_active;
    if (activeFilter === 'inactive') return !loc.is_active;
    return true;
  });

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-brand-dark">Location Management</h1>
                <p className="text-sm text-brand-neutral">{allLocations.length} locations total</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-brand-light text-brand-dark rounded-lg text-sm font-medium hover:bg-brand-light/50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                Sync from Zuper
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Location
              </button>
            </div>
          </div>

          {/* Active/Inactive/All filter */}
          <div className="flex items-center gap-1 mb-4 bg-white border border-brand-light rounded-xl p-1 w-fit">
            {['active', 'inactive', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                  activeFilter === f
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'text-brand-neutral hover:text-brand-dark'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {syncMutation.isSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              Synced from Zuper: {syncMutation.data?.data?.synced?.created || 0} created, {syncMutation.data?.data?.synced?.updated || 0} updated
            </div>
          )}

          {/* Locations List */}
          {isLoading ? (
            <div className="text-center py-12 text-brand-neutral">Loading locations...</div>
          ) : (
            <div className="space-y-3">
              {locations.map(loc => (
                <div key={loc.id} className="bg-white rounded-xl border border-brand-light/60 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-brand-primary" />
                        <span className="font-semibold text-brand-dark">{loc.name}</span>
                        {loc.is_default ? (
                          <span className="px-2 py-0.5 bg-brand-primary/10 text-brand-primary text-[10px] font-bold rounded-full">Default</span>
                        ) : null}
                        {!loc.is_active && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded-full">Inactive</span>
                        )}
                      </div>
                      {loc.address && <p className="text-sm text-brand-neutral mt-1">{loc.address}</p>}
                      {loc.zuper_property_uid && (
                        <p className="text-[11px] text-brand-neutral/60 mt-1 font-mono">Zuper: {loc.zuper_property_uid}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          setEditingLocation(loc);
                          setForm({ name: loc.name, address: loc.address || '', zuperPropertyUid: loc.zuper_property_uid || '' });
                        }}
                        className="p-1.5 text-brand-neutral hover:text-brand-primary hover:bg-brand-light/60 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {!loc.is_default && (
                        <button
                          onClick={() => {
                            if (confirm(`Deactivate location "${loc.name}"?`)) {
                              deleteMutation.mutate(loc.id);
                            }
                          }}
                          className="p-1.5 text-brand-neutral hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create / Edit Modal */}
          {(showCreateModal || editingLocation) && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-lg font-bold text-brand-dark mb-4">
                  {editingLocation ? 'Edit Location' : 'Create Location'}
                </h2>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (editingLocation) {
                    updateMutation.mutate({ id: editingLocation.id, name: form.name, address: form.address || null, zuperPropertyUid: form.zuperPropertyUid || null });
                  } else {
                    createMutation.mutate(form);
                  }
                }} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Location name"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Address (optional)"
                    value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Zuper Property UID (optional)"
                    value={form.zuperPropertyUid}
                    onChange={e => setForm(f => ({ ...f, zuperPropertyUid: e.target.value }))}
                    className="w-full px-3 py-2 border border-brand-light rounded-lg text-sm font-mono"
                  />
                  <div className="flex gap-2 pt-2">
                    <button type="submit" className="flex-1 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary/90">
                      {editingLocation ? 'Save' : 'Create'}
                    </button>
                    <button type="button" onClick={() => { setShowCreateModal(false); setEditingLocation(null); }} className="flex-1 py-2 bg-gray-100 text-brand-dark rounded-lg text-sm font-medium hover:bg-gray-200">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
